import { Info, Trash2 } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React, { useCallback } from "react";
import { Pressable } from "react-native";
import { XStack, YStack } from "tamagui";
import {
  type SortableItemWithId,
  SortableList,
} from "../../../core/components/SortableList";
import { Body } from "../../../ui/Body";
import { Box } from "../../../ui/Box";
import { CenterContent } from "../../../ui/CenterContent";
import { Group } from "../../../ui/Group";
import { Label } from "../../../ui/Label";
import { PopoverConfirm } from "../../../ui/PopoverConfirm";
import type { CustomerProfileQuestion } from "../customers/BusinessCustomerModel";
import type { BusinessTemplateModel } from "./BusinessTemplateModel";

export const BusinessAdminTemplateQuestions = observer(
  ({ template }: { template: BusinessTemplateModel }) => {
    const renderItem = useCallback(
      (listItem: SortableItemWithId<CustomerProfileQuestion>) => {
        const question = listItem.item;

        return (
          <XStack
            key={question.uid}
            gap={"$medium"}
            justifyContent={"space-between"}
            alignItems={"center"}
            width={"100%"}
          >
            <Body>{question.question}</Body>

            <PopoverConfirm
              onConfirm={() => {
                template.removeProfileQuestion(question);
              }}
            >
              <Trash2 size={"$size.normal"} color={"$muted"} />
            </PopoverConfirm>
          </XStack>
        );
      },
      [template],
    );

    return (
      <YStack gap={"$medium"}>
        <Box>
          <Label>
            Active questions ({template.data.profileQuestions.length})
          </Label>
          <SortableList
            items={template.data.profileQuestions}
            onChange={(items) => {
              template?.updateProfileQuestionsOrder(items.map((i) => i.item));
            }}
            renderItem={renderItem}
          />
          {template.data.profileQuestions.length === 0 && (
            <CenterContent
              icon={Info}
              muted
              title={"No questions selected yet"}
              subTitle={
                "This is fine as well, but select any below if you want to enable this feature"
              }
            />
          )}
        </Box>

        <Box>
          <Label>Available questions</Label>

          <Body muted>
            Tap to add a question to this template. Add new ones in your profile
            settings.
          </Body>

          <Group>
            {template.customer.data.profileQuestions.map((question) => {
              const isInUse = !!template.data.profileQuestions.find(
                (q) => q.uid === question.uid,
              );

              return (
                <Pressable
                  disabled={isInUse}
                  style={{ flex: 1 }}
                  key={question.uid}
                  onPress={() => template.addProfileQuestion(question)}
                >
                  <XStack
                    gap={"$medium"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                    width={"100%"}
                  >
                    <Body muted={isInUse}>{question.question}</Body>
                  </XStack>
                </Pressable>
              );
            })}
          </Group>
        </Box>
      </YStack>
    );
  },
);
